// HT-3 · 検証セット（学習に使った書類 / 検証用の書類 / 守るべき書類）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LEARN, API, authHeader, detailOf, Panel, Pill, Empty, Help } from './shared';
import CoverageGaps from './CoverageGaps';

const PROFILES = [
  ['mechanical_drawing', '機械図面'], ['electrical_drawing', '電気図面'],
  ['circuit_diagram', '回路図'], ['wiring_diagram', '配線図'],
  ['block_diagram', 'ブロック図'], ['flowchart', 'フローチャート'],
];

function DocumentPicker({ onPick, busy, existingIds = [] }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const search = async () => {
    if (!q.trim()) return;
    try {
      const r = await axios.get(`${API}/api/dms/documents`,
        { params: { q: q.trim(), limit: 8 }, headers: authHeader() });
      const items = r.data?.items || r.data || [];
      setHits(items.slice(0, 8));
      if (!items.length) toast.info('該当する書類がありません');
    } catch (e) { toast.error(detailOf(e, '書類を検索できませんでした')); }
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          placeholder="書類名や図番で検索" aria-label="検証に使う書類を検索"
          className="h-9 min-w-[200px] flex-1 rounded-md border border-zinc-200 px-2 text-[12px]"
          data-testid="vset-doc-search-input" />
        <Button variant="outline" className="h-9 text-[12px]" onClick={search}
          data-testid="vset-doc-search">
          <MagnifyingGlass size={13} className="mr-1" />検索
        </Button>
      </div>
      {hits.map((h) => (
        <div key={h.id} className="flex flex-wrap items-center justify-between gap-2
          rounded-md border border-zinc-200 px-2.5 py-1.5"
          data-testid={`vset-doc-hit-${h.id}`}>
          <span className="text-[11.5px] text-zinc-800">{h.title || h.id}</span>
          {existingIds.includes(h.id) ? (
            <span className="text-[11.5px] text-zinc-500"
              data-testid={`vset-doc-added-${h.id}`}>追加済み</span>
          ) : (
            <Button size="sm" className="h-8 bg-teal-700 text-[11.5px] hover:bg-teal-800"
              disabled={busy} onClick={() => { onPick(h.id); setHits([]); setQ(''); }}
              data-testid={`vset-doc-add-${h.id}`}>この書類を追加</Button>
          )}
        </div>
      ))}
    </div>
  );
}

function SetCard({ row, onAddDoc, onRemoveDoc, onDelete, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-zinc-200 px-3 py-2.5"
      data-testid={`vset-card-${row.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12.5px] font-medium text-zinc-900">{row.name}</p>
          <p className="flex flex-wrap gap-1.5 pt-1.5">
            <Pill testId={`vset-category-${row.id}`}>{row.category_label_ja}</Pill>
            <Pill tone={row.real_document_count ? 'teal' : 'amber'}>{row.source_label_ja}</Pill>
            <Pill tone={row.status === 'ready' ? 'teal' : 'amber'}>{row.status_label_ja}</Pill>
            <Pill>{`書類 ${row.document_count}件（実書類 ${row.real_document_count}件・期待値あり ${row.expected_document_count}件）`}</Pill>
          </p>
          <p className="pt-1.5 text-[11.5px] text-zinc-700">{row.scope || '範囲の記載なし'}</p>
          <p className="text-[11.5px] text-zinc-600">
            対象: {row.profiles_labels_ja?.join(' / ') || '指定なし'}
          </p>
          <p className="text-[11px] text-zinc-500">
            担当 {row.owner || '—'} · 最終実行 {row.last_run_at ? String(row.last_run_at).slice(0, 16) : '未実行'}
            {' · '}前回の結果 {row.last_result_ja}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-9 text-[12px]"
            onClick={() => setOpen(!open)} data-testid={`vset-open-${row.id}`}>
            {open ? '閉じる' : '書類を見る'}
          </Button>
          {row.created_by !== 'system' && (
            <Button size="sm" variant="outline"
              className="h-9 border-rose-200 text-[12px] text-rose-700 hover:bg-rose-50"
              disabled={busy} onClick={() => onDelete(row.id)}
              data-testid={`vset-delete-${row.id}`}>
              <Trash size={12} className="mr-1" />削除
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div className="space-y-1.5 pt-2">
          <div className="grid gap-x-4 sm:grid-cols-3">
            {row.profile_breakdown.map((b) => (
              <p key={b.profile_key} className="text-[11px] text-zinc-600"
                data-testid={`vset-breakdown-${row.id}-${b.profile_key}`}>
                {b.label_ja}: {b.count}件（実書類 {b.real_count}件）
              </p>
            ))}
          </div>
          {(row.documents || []).length === 0 ? (
            <p className="text-[11.5px] text-zinc-500">書類がありません。</p>
          ) : (
            <ul className="space-y-1">
              {(row.documents || []).map((d) => (
                <li key={d.document_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md
                    border border-zinc-100 bg-zinc-50/60 px-2.5 py-1.5 text-[11.5px]"
                  data-testid={`vset-doc-${row.id}-${d.document_id}`}>
                  <span className="min-w-0 text-zinc-800">
                    {d.title || d.document_id}
                    <span className="pl-2 text-zinc-500">
                      {d.source === 'real' ? '実書類' : '合成データ'}
                      {d.negative_control ? '・誤りやすい例' : ''}
                      {Object.keys(d.expected_fields || {}).length
                        ? `・期待値 ${Object.keys(d.expected_fields).length}項目` : '・期待値なし'}
                    </span>
                  </span>
                  {d.source === 'real' && (
                    <button type="button" onClick={() => onRemoveDoc(row.id, d.document_id)}
                      className="min-h-[28px] text-teal-700 hover:underline"
                      data-testid={`vset-doc-remove-${d.document_id}`}>外す</button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {row.category !== 'protected_regression' && (
            <DocumentPicker busy={busy} onPick={(docId) => onAddDoc(row.id, docId)}
              existingIds={(row.documents || []).map((d) => d.document_id)} />
          )}
        </div>
      )}
    </div>
  );
}

export default function ValidationSets({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${LEARN}/validation-sets`, { headers: authHeader() });
      setData(r.data);
    } catch (e) { toast.error(detailOf(e, '検証セットを取得できませんでした')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      onChanged?.();
    } catch (e) { toast.error(detailOf(e, '処理できませんでした')); } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="space-y-4">
      <CoverageGaps onChanged={load} />

      <Panel title="検証セット" testId="learn-validation-sets"
        hint="学習に使った書類・検証用の書類・守るべき書類を分けて管理します。"
        action={(
          <div className="flex items-center gap-1.5">
            <Help label="検証セット" testId="vset-help">
              検証用の書類は、学習に使った書類とは別に用意します。守るべき書類は、これまで正しく
              読めていた内容が悪化しないことを確かめるための集合です。
            </Help>
            <Button className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
              onClick={() => setForm(form ? null : {
                name: '', category: 'validation', scope: '',
                profiles: [], source: 'real',
              })}
              data-testid="vset-new">
              <Plus size={13} className="mr-1" />検証セットを作る
            </Button>
          </div>
        )}>
        {form && (
          <div className="mb-3 space-y-1.5 rounded-md border border-zinc-200 p-2.5"
            data-testid="vset-form">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="名前（例: 電気図面（実書類））" aria-label="検証セットの名前"
              className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
              data-testid="vset-form-name" />
            <select value={form.category} aria-label="分類"
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
              data-testid="vset-form-category">
              {(data.categories || []).map((c) => (
                <option key={c.key} value={c.key}>{c.label_ja}</option>
              ))}
            </select>
            <input value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
              placeholder="範囲（例: 旧様式のタイトル欄）" aria-label="範囲"
              className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
              data-testid="vset-form-scope" />
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {PROFILES.map(([key, label]) => (
                <label key={key} className="flex items-center gap-1.5 text-[11.5px] text-zinc-800">
                  <input type="checkbox" className="h-4 w-4"
                    checked={form.profiles.includes(key)}
                    onChange={(e) => setForm({
                      ...form,
                      profiles: e.target.checked ? [...form.profiles, key]
                        : form.profiles.filter((p) => p !== key),
                    })}
                    data-testid={`vset-form-profile-${key}`} />
                  {label}
                </label>
              ))}
            </div>
            <select value={form.source} aria-label="書類の種別"
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="h-9 w-full rounded-md border border-zinc-200 px-2 text-[12px]"
              data-testid="vset-form-source">
              <option value="real">実書類</option>
              <option value="synthetic">合成データ</option>
            </select>
            <Button className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800" disabled={busy}
              onClick={() => act(async () => {
                await axios.post(`${LEARN}/validation-sets`, form, { headers: authHeader() });
                setForm(null);
              }, '検証セットを作りました')}
              data-testid="vset-form-save">作る</Button>
          </div>
        )}
        {(data.rows || []).length === 0 ? (
          <Empty testId="vset-empty">検証セットはまだありません。</Empty>
        ) : (
          <div className="space-y-1.5">
            {data.rows.map((row) => (
              <SetCard key={row.id} row={row} busy={busy}
                onAddDoc={(sid, documentId) => act(() => axios.post(
                  `${LEARN}/validation-sets/${sid}/documents`, { document_id: documentId },
                  { headers: authHeader() }), '検証用の書類に追加しました')}
                onRemoveDoc={(sid, documentId) => act(() => axios.delete(
                  `${LEARN}/validation-sets/${sid}/documents/${documentId}`,
                  { headers: authHeader() }), '書類を外しました')}
                onDelete={(sid) => act(() => axios.delete(
                  `${LEARN}/validation-sets/${sid}`, { headers: authHeader() }),
                  '検証セットを削除しました')} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
